import { shouldOfferRenew } from "./allowance";
import { cancelOffer, fetchWalletBudget, replaceWallet, requestManageLink, retryOffer, signedTermsStep, updateSpendingCap, usesSignedTerms } from "./api";
import type { SubscriptionCharge, SubscriptionOffer } from "./types";
import { ICONS, confirmDialog, dangerButton, errorBox, escapeAttr, escapeHtml, hint, infoBox, loadingRow, primaryButton, summaryList } from "./ui";
import { connectAndApprove, dismissWalletModal, InsufficientFundsError, readAllowance, revokeAllowance, walletConnectedAs } from "./wallet";

function closeDialog(dialog: HTMLDialogElement | null): Promise<void> {
  // Closing a modal dialog retargets the same click onto the page and AppKit treats it as dismiss.
  const block = (event: Event) => {
    event.preventDefault();
    event.stopPropagation();
  };
  document.addEventListener("click", block, true);
  document.addEventListener("pointerup", block, true);
  if (dialog?.open) dialog.close();
  return new Promise((resolve) => {
    window.setTimeout(() => {
      document.removeEventListener("click", block, true);
      document.removeEventListener("pointerup", block, true);
      resolve();
    }, 300);
  });
}

function showWalletPending(root: HTMLElement, message: string): void {
  root.querySelector("#wallet-pending")?.remove();
  const host = root.querySelector(".pay-body") || root;
  host.insertAdjacentHTML("beforeend", `<div id="wallet-pending">${loadingRow(message)}</div>`);
}

function clearWalletPending(root: HTMLElement): void {
  root.querySelector("#wallet-pending")?.remove();
}

/**
 * Wallet progress inside a modal dialog, in place of its form.
 * AppKit's connect window would sit under the modal, so the dialog steps aside until a wallet is picked.
 */
function dialogStatus(root: HTMLElement, dialog: HTMLDialogElement, form: HTMLElement | null, slot: HTMLElement | null) {
  let inDialog = false;
  let busy = false;
  // The wallet request keeps running after a close, and nothing else on the page would show it.
  dialog.addEventListener("cancel", (event) => {
    if (busy) event.preventDefault();
  });
  const status = (message: string) => {
    if (!inDialog || !form || !slot) {
      showWalletPending(root, message);
      return;
    }
    clearWalletPending(root);
    dialog.querySelectorAll(".msg").forEach((node) => node.remove());
    form.hidden = true;
    slot.hidden = false;
    slot.innerHTML = loadingRow(message);
    if (!dialog.open) dialog.showModal();
  };
  return {
    status,
    async start(account?: string | null) {
      busy = true;
      inDialog = walletConnectedAs(account);
      if (!inDialog) await closeDialog(dialog);
      status("Opening wallet…");
    },
    connected() {
      inDialog = true;
    },
    reset() {
      busy = false;
      inDialog = false;
      clearWalletPending(root);
      if (form) form.hidden = false;
      if (slot) {
        slot.hidden = true;
        slot.innerHTML = "";
      }
    },
  };
}

function walletCancelled(error: unknown): boolean {
  return error instanceof Error && error.message === "Wallet connection cancelled";
}

function intervalLabel(seconds: number): string {
  if (seconds >= 31536000 && seconds % 31536000 === 0) {
    const n = seconds / 31536000;
    return n === 1 ? "every year" : `every ${n} years`;
  }
  if (seconds >= 2592000 && seconds % 2592000 === 0) {
    const n = seconds / 2592000;
    return n === 1 ? "every month" : `every ${n} months`;
  }
  if (seconds >= 86400 && seconds % 86400 === 0) {
    const n = seconds / 86400;
    return n === 1 ? "every day" : `every ${n} days`;
  }
  if (seconds >= 3600 && seconds % 3600 === 0) {
    const n = seconds / 3600;
    return n === 1 ? "every hour" : `every ${n} hours`;
  }
  return `every ${seconds}s`;
}

export function when(ts?: number | null): string {
  if (!ts) return "—";
  return new Date(ts * 1000).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function explorerTx(chainId: number, hash?: string | null): string | null {
  if (!hash) return null;
  const bases: Record<number, string> = {
    1: "https://etherscan.io/tx/",
    137: "https://polygonscan.com/tx/",
    56: "https://bscscan.com/tx/",
    42161: "https://arbiscan.io/tx/",
    8453: "https://basescan.org/tx/",
    11155111: "https://sepolia.etherscan.io/tx/",
    80002: "https://amoy.polygonscan.com/tx/",
  };
  const base = bases[chainId];
  return base ? `${base}${hash}` : null;
}

function chargeMeta(status: string): { label: string; tone: string; icon: string } {
  const value = (status || "").toLowerCase();
  if (value === "paid" || value === "success") {
    return { label: "Paid", tone: "paid", icon: ICONS.check };
  }
  if (value === "pending" || value === "processing" || value === "confirming") {
    return { label: "Pending", tone: "pending", icon: ICONS.clock };
  }
  if (value === "failed" || value === "error") {
    return { label: "Failed", tone: "failed", icon: ICONS.x };
  }
  if (value === "expired") {
    return { label: "Expired", tone: "muted", icon: ICONS.clock };
  }
  const label = value ? value.replace(/_/g, " ") : "Charge";
  return { label: label[0].toUpperCase() + label.slice(1), tone: "muted", icon: ICONS.clock };
}

function formatAmount(value: string): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 8 });
}

function formatRaw(raw: bigint, decimals: number, digits = Math.min(6, decimals)): string {
  const scale = 10n ** BigInt(Math.max(0, decimals));
  const whole = raw / scale;
  const frac = (raw % scale).toString().padStart(decimals, "0").replace(/0+$/, "");
  const text = frac ? `${whole.toString()}.${frac}` : whole.toString();
  const n = Number(text);
  if (!Number.isFinite(n)) return text;
  return n.toLocaleString("en-US", { maximumFractionDigits: digits });
}

function humanToRaw(human: number, decimals: number): bigint {
  const fixed = human.toFixed(Math.min(8, Math.max(0, decimals)));
  const [whole, frac = ""] = fixed.split(".");
  const digits = `${frac}${"0".repeat(decimals)}`.slice(0, decimals);
  return BigInt(whole || "0") * 10n ** BigInt(decimals) + BigInt(digits || "0");
}

function signedLimitText(offer: SubscriptionOffer): string {
  return (offer.mandate_terms || "").replace(/\s*Cancel anytime\.?\s*$/i, "").trim();
}

function signedLimitPanel(offer: SubscriptionOffer): string {
  if (!["active", "past_due"].includes(offer.status)) return "";
  const text = signedLimitText(offer);
  if (!text) return "";
  const renew =
    offer.can_manage !== false && offer.payer_address && offer.spender && offer.token_contract && offer.chain_id
      ? `<p class="hint cap-note" id="renew-note" hidden>The wallet allowance is too low for the next charge. Renew the approval to continue.</p>
         <button type="button" class="btn-quiet" id="renew-approve" hidden>Renew approval</button>
         <div id="renew-pending"></div>`
      : "";
  return `<section class="cap-panel" id="terms-panel">
    <div class="cap-head">
      <h3>Signed limit</h3>
    </div>
    <p class="terms-limit">${escapeHtml(text)}</p>
    ${renew}
  </section>`;
}

function spendingCapPanel(offer: SubscriptionOffer): string {
  if (usesSignedTerms(offer)) return signedLimitPanel(offer);
  if (!["active", "past_due"].includes(offer.status) || offer.can_manage === false) return "";
  if (!offer.payer_address || !offer.spender || !offer.token_contract || !offer.chain_id) return "";
  const capRaw = offer.spending_cap_raw || offer.approve_amount_raw;
  if (!capRaw || capRaw === "0") return "";
  const cap = BigInt(capRaw);
  const human = Number(formatRaw(cap, offer.token_decimals).replace(/,/g, ""));
  const max = human * 4;
  const step = human >= 100 ? 1 : 0.01;
  const label = `${formatRaw(cap, offer.token_decimals)} ${offer.token_symbol}`;
  return `<section class="cap-panel" id="cap-panel">
    <div class="cap-head">
      <h3>Spending cap</h3>
      <span id="cap-left">Checking allowance…</span>
    </div>
    <div class="cap-track" aria-hidden="true"><span class="cap-fill" id="cap-fill"></span></div>
    <p class="hint cap-note" id="cap-note" hidden></p>
    <button type="button" class="btn-quiet" id="cap-open">Change spending cap</button>
    <dialog class="cap-dialog" id="cap-dialog">
      <h3>New spending cap</h3>
      <div id="cap-form">
        <p class="hint">Starts at the current cap. Later charges still come from this subscription wallet.</p>
        <label class="cap-slider-label" for="cap-slider">
          <span>New cap</span>
          <strong id="cap-chosen">${escapeHtml(label)}</strong>
        </label>
        <input id="cap-slider" type="range" min="${human}" max="${max}" step="${step}" value="${human}" />
        ${primaryButton("cap-approve", "Update spending cap")}
        <button type="button" class="btn-quiet" id="cap-close">Close</button>
      </div>
      <div class="cap-pending" id="cap-pending" hidden></div>
    </dialog>
  </section>`;
}

function chargeRow(offer: SubscriptionOffer, charge: SubscriptionCharge): string {
  const meta = chargeMeta(charge.status);
  const link = explorerTx(offer.chain_id, charge.tx_hash);
  const amount = charge.invoice_amount
    ? `${formatAmount(charge.invoice_amount)} ${offer.token_symbol}`
    : "";
  const tx = link
    ? `<a class="charge-tx" href="${escapeHtml(link)}" target="_blank" rel="noopener">${ICONS.external}<span>View tx</span></a>`
    : "";
  return `<article class="charge-item is-${meta.tone}">
    <div class="charge-ico" aria-hidden="true">${meta.icon}</div>
    <div class="charge-main">
      <strong>${escapeHtml(meta.label)}</strong>
      <small>${escapeHtml(when(charge.created_at))}</small>
    </div>
    <div class="charge-side">
      ${amount ? `<span class="charge-amount">${escapeHtml(amount)}</span>` : ""}
      ${tx}
    </div>
  </article>`;
}

function historySection(offer: SubscriptionOffer): string {
  const plans = offer.plan_history || [];
  const actions = offer.can_manage === false ? [] : offer.action_history || [];
  if (!plans.length && !actions.length) return "";
  const planRows = plans
    .map((item) => {
      const until = item.ended_at ? `until ${when(item.ended_at)}` : "current";
      return `<article class="charge-item">
        <div class="charge-main">
          <strong>${escapeHtml(item.title)}</strong>
          <small>${escapeHtml(when(item.started_at))} · ${escapeHtml(until)}</small>
        </div>
        <div class="charge-side">
          <span class="charge-amount">${escapeHtml(item.amount_fiat)} ${escapeHtml(item.currency_fiat)}</span>
        </div>
      </article>`;
    })
    .join("");
  const actionRows = [...actions]
    .reverse()
    .map(
      (item) => `<article class="charge-item">
        <div class="charge-main">
          <strong>${escapeHtml(when(item.at))}</strong>
          <small>${escapeHtml(item.explanation)}</small>
        </div>
      </article>`
    )
    .join("");
  return `<section class="charge-list">
    ${
      planRows
        ? `<div class="charge-head"><h3>Plans</h3></div>${planRows}`
        : ""
    }
    ${
      actionRows
        ? `<div class="charge-head"><h3>Activity</h3></div>${actionRows}`
        : ""
    }
  </section>`;
}

function visibleCharges(offer: SubscriptionOffer): SubscriptionCharge[] {
  return (offer.charges || []).filter((charge) => {
    const status = (charge.status || "").toLowerCase();
    return status !== "expired" && status !== "failed" && status !== "error";
  });
}

export function retryMessage(offer: SubscriptionOffer): { title: string; body: string } {
  const symbol = offer.token_symbol || "token";
  const amount = `${offer.amount_fiat} ${offer.currency_fiat}`;
  switch (offer.last_error_code) {
    case "insufficient_balance":
      return {
        title: "Not enough balance",
        body:
          offer.last_error ||
          `The wallet does not have enough ${symbol} to pay ${amount}. Top up and retry the payment.`,
      };
    case "allowance_low":
    case "insufficient_allowance":
      return {
        title: "Allowance is too low",
        body:
          offer.last_error ||
          `The wallet allowance is too low to pull ${symbol}. Approve again, then retry.`,
      };
    default:
      return {
        title: "Payment did not go through",
        body: offer.last_error || "The last charge could not be completed. You can try again.",
      };
  }
}

export function isRetryable(offer: SubscriptionOffer): boolean {
  return offer.status === "past_due";
}

export function isFirstPaymentFailed(offer: SubscriptionOffer): boolean {
  return isRetryable(offer) && !offer.cycles_charged;
}

function accessNote(offer: SubscriptionOffer): string {
  return offer.cycles_charged ? " Access stays until the paid period ends." : "";
}

function firstPaymentBody(offer: SubscriptionOffer, extra: string): string {
  const retry = retryMessage(offer);
  const locked = offer.can_manage === false;
  return `
    <div class="retry-panel">
      <h2 class="retry-title">${escapeHtml(retry.title)}</h2>
      <p class="retry-sub">${escapeHtml(retry.body)}</p>
      ${locked ? "" : primaryButton("retry-pay", "Retry payment")}
    </div>
    ${
      locked
        ? manageLinkSlot(offer.subscription_id)
        : `<button type="button" class="btn-quiet" id="cancel-sub">Cancel subscription</button>
           ${hint("No funds were taken. Retrying uses the approval you already gave.")}`
    }
    ${extra}
  `;
}

export function isCancelScheduled(offer: SubscriptionOffer): boolean {
  return Boolean(offer.cancel_at) && offer.status !== "cancelled" && offer.status !== "exhausted";
}

function shortAddress(value: string): string {
  if (value.length < 12) return value;
  return `${value.slice(0, 6)}…${value.slice(-4)}`;
}

function walletCell(offer: SubscriptionOffer): string {
  if (!offer.payer_address) return "";
  const canEdit =
    offer.can_manage !== false &&
    !isCancelScheduled(offer) &&
    ["active", "past_due"].includes(offer.status);
  const edit = canEdit
    ? `<button type="button" class="icon-btn" id="edit-wallet" aria-label="Change wallet">${ICONS.edit}</button>`
    : "";
  return `<span class="wallet-cell"><span>${escapeHtml(shortAddress(offer.payer_address))}</span>${edit}</span>`;
}

function walletDialog(offer: SubscriptionOffer): string {
  if (!offer.payer_address) return "";
  const canEdit =
    offer.can_manage !== false &&
    !isCancelScheduled(offer) &&
    ["active", "past_due"].includes(offer.status);
  if (!canEdit) return "";
  return `<dialog class="cap-dialog" id="wallet-dialog">
    <h3 id="wallet-dialog-title">Change wallet</h3>
    <div id="wallet-dialog-form">
      <p class="hint" id="wallet-dialog-body">The new wallet will approve the current spending cap. Future charges will be pulled from it.</p>
      <div id="wallet-dialog-error"></div>
      ${primaryButton("wallet-continue", "Continue")}
      <button type="button" class="btn-quiet" id="wallet-skip" hidden>Skip</button>
      <button type="button" class="btn-quiet" id="wallet-close">Close</button>
    </div>
    <div class="cap-pending" id="wallet-dialog-pending" hidden></div>
  </dialog>`;
}

export function manageBody(offer: SubscriptionOffer, extra = ""): string {
  if (isFirstPaymentFailed(offer)) return firstPaymentBody(offer, extra);
  const retryable = isRetryable(offer);
  const ending = isCancelScheduled(offer);
  const canCancel =
    offer.can_manage !== false &&
    !ending &&
    ["active", "past_due", "pending_approve", "awaiting_charge", "confirming"].includes(offer.status);
  const charges = retryable ? visibleCharges(offer) : offer.charges || [];
  const retry = retryable ? retryMessage(offer) : null;
  const list = charges.length
    ? `<section class="charge-list">
        <div class="charge-head">
          <h3>Charges</h3>
          <span class="charge-count">${charges.length}</span>
        </div>
        ${charges.map((c) => chargeRow(offer, c)).join("")}
      </section>`
    : retryable
    ? ""
    : `<p class="hint">No charges yet. The first pull runs after approve, then on the timer.</p>`;
  const wallet = walletCell(offer);
  const walletRow = wallet ? [{ label: "Wallet", value: "", html: wallet }] : [];
  const details = retryable
    ? summaryList(walletRow)
    : summaryList([
        { label: "Status", value: ending ? "active · cancels at period end" : offer.status },
        ...(offer.customer_name ? [{ label: "Name", value: offer.customer_name }] : []),
        ...(offer.customer_email ? [{ label: "Email", value: offer.customer_email }] : []),
        ...(offer.reference ? [{ label: "Reference", value: offer.reference }] : []),
        { label: "Amount", value: `${offer.amount_fiat} ${offer.currency_fiat}` },
        { label: "Frequency", value: intervalLabel(offer.interval_seconds) },
        {
          label: "Charged",
          value:
            offer.cycles_authorized > 0
              ? `${offer.cycles_charged} / ${offer.cycles_authorized}`
              : `${offer.cycles_charged} / until cancelled`,
        },
        ...walletRow,
        ending
          ? { label: "Access until", value: when(offer.cancel_at) }
          : { label: "Next charge", value: when(offer.next_charge_at) },
      ]);
  const retryBlock = retry && offer.can_manage !== false
    ? `<div class="retry-panel">
        <h2 class="retry-title">${escapeHtml(retry.title)}</h2>
        <p class="retry-sub">${escapeHtml(retry.body)}</p>
        ${primaryButton("retry-pay", "Retry payment")}
      </div>`
    : "";
  const cancelHint = ending
    ? hint("Cancellation is scheduled. Access stays until the paid period ends. No further charges will be pulled.")
    : canCancel
      ? usesSignedTerms(offer)
        ? hint(`Cancel revokes the signed terms on-chain and stops future charges.${accessNote(offer)}`)
        : hint(`Cancel asks your wallet to revoke the token allowance and stops future charges.${accessNote(offer)}`)
      : "";
  return `
    ${retryBlock}
    ${details}
    ${spendingCapPanel(offer)}
    ${historySection(offer)}
    ${list}
    ${canCancel ? dangerButton("cancel-sub", "Cancel subscription") : ""}
    ${cancelHint}
    ${offer.can_manage === false ? manageLinkSlot(offer.subscription_id) : ""}
    ${walletDialog(offer)}
    ${extra}
  `;
}

export function manageLinkSlot(subscriptionId: string): string {
  return `<div id="manage-link-slot" data-subscription-id="${escapeAttr(subscriptionId)}">${primaryButton("manage-link", "Manage subscription")}</div>`;
}

export function bindManageLink(root: ParentNode): void {
  const button = root.querySelector<HTMLButtonElement>("#manage-link");
  const slot = root.querySelector<HTMLElement>("#manage-link-slot");
  if (!button || !slot) return;
  button.addEventListener("click", () => {
    const id = slot.dataset.subscriptionId || "";
    if (!id || button.disabled) return;
    button.disabled = true;
    button.textContent = "Sending the link…";
    void (async () => {
      try {
        const masked = await requestManageLink(id);
        slot.innerHTML = infoBox(
          `We sent a link to manage this subscription to ${masked}. Open it in this browser within 24 hours. After you open it, it keeps working.`
        );
      } catch (error) {
        button.disabled = false;
        button.textContent = "Manage subscription";
        slot.querySelectorAll(".msg-danger").forEach((node) => node.remove());
        const msg = error instanceof Error ? error.message : "Could not send the link";
        slot.insertAdjacentHTML("beforeend", errorBox(msg));
      }
    })();
  });
}

function bindSpendingCap(root: HTMLElement, offer: SubscriptionOffer, onUpdate: (next: SubscriptionOffer) => void): void {
  const slider = root.querySelector<HTMLInputElement>("#cap-slider");
  const chosen = root.querySelector<HTMLElement>("#cap-chosen");
  const button = root.querySelector<HTMLButtonElement>("#cap-approve");
  const left = root.querySelector<HTMLElement>("#cap-left");
  const fill = root.querySelector<HTMLElement>("#cap-fill");
  const note = root.querySelector<HTMLElement>("#cap-note");
  if (!slider || !button || !offer.payer_address || !offer.spender || !offer.token_contract) return;
  const decimals = offer.token_decimals;
  const symbol = offer.token_symbol;
  const floor = BigInt(offer.spending_cap_raw || offer.approve_amount_raw || "0");
  // The allowance is shared by every subscription this wallet pays, so the budget comes from the server.
  const budget = offer.budget_left_raw != null ? BigInt(offer.budget_left_raw) : null;
  let remaining = budget ?? floor;
  const paint = (allowance: bigint | null) => {
    remaining = budget ?? allowance ?? floor;
    if (allowance != null && allowance < remaining) remaining = allowance;
    const pct = floor === 0n ? 0 : Number((remaining * 10_000n) / floor) / 100;
    const width = Math.max(0, Math.min(100, pct));
    if (fill) {
      fill.style.width = `${width}%`;
      fill.classList.remove("is-high", "is-mid", "is-low");
      fill.classList.add(width >= 50 ? "is-high" : width >= 30 ? "is-mid" : "is-low");
    }
    if (left) {
      left.textContent = `${formatRaw(remaining, decimals, 2)} of ${formatRaw(floor, decimals, 2)} ${symbol} left`;
    }
    if (note) {
      const short = budget != null && allowance != null && allowance < budget;
      note.hidden = !short;
      note.textContent = short
        ? `The wallet allows only ${formatRaw(allowance, decimals, 2)} ${symbol}. Update the spending cap to restore it.`
        : "";
    }
  };
  const syncButton = () => {
    const next = humanToRaw(Number(slider.value), decimals);
    if (chosen) chosen.textContent = `${formatRaw(next, decimals)} ${symbol}`;
    button.disabled = next < floor || (next === floor && remaining >= floor);
  };
  slider.addEventListener("input", syncButton);
  button.disabled = true;
  paint(null);
  void readAllowance({
    chainId: offer.chain_id,
    token: offer.token_contract,
    owner: offer.payer_address,
    spender: offer.spender,
  })
    .then((allowance) => {
      paint(allowance);
      syncButton();
    })
    .catch(() => {
      if (budget == null && left) left.textContent = "Could not read the remaining allowance";
      syncButton();
    });
  const dialog = root.querySelector<HTMLDialogElement>("#cap-dialog");
  const pending = dialog
    ? dialogStatus(root, dialog, root.querySelector<HTMLElement>("#cap-form"), root.querySelector<HTMLElement>("#cap-pending"))
    : null;
  root.querySelector("#cap-open")?.addEventListener("click", () => dialog?.showModal());
  root.querySelector("#cap-close")?.addEventListener("click", () => dialog?.close());
  let capSeq = 0;
  button.addEventListener("click", () => {
    const next = humanToRaw(Number(slider.value), decimals);
    if (next < floor || (next === floor && remaining >= floor)) return;
    const seq = ++capSeq;
    button.disabled = true;
    void (async () => {
      try {
        const payer = offer.payer_address;
        if (!payer) throw new Error("This subscription has no paying wallet yet");
        await pending?.start(payer);
        const { tx } = await connectAndApprove({
          chainId: offer.chain_id,
          token: offer.token_contract,
          spender: offer.spender,
          amountRaw: next,
          account: payer,
          otherBudgets: (wallet) => fetchWalletBudget(offer.subscription_id, wallet),
          isCancelled: () => seq !== capSeq,
          onConnected: () => pending?.connected(),
          onStatus: (message) => (pending ? pending.status(message) : showWalletPending(root, message)),
        });
        pending?.status("Saving the spending cap…");
        onUpdate(await updateSpendingCap(offer.subscription_id, next.toString(), tx));
      } catch (error) {
        if (seq !== capSeq) return;
        pending?.reset();
        button.disabled = false;
        if (walletCancelled(error)) {
          if (dialog && !dialog.open) dialog.showModal();
          return;
        }
        const msg = error instanceof Error ? error.message : "Approve failed";
        dialog?.querySelectorAll(".msg").forEach((node) => node.remove());
        dialog?.insertAdjacentHTML("beforeend", errorBox(msg));
        if (dialog && !dialog.open) dialog.showModal();
      }
    })();
  });
}

function bindWalletEdit(root: HTMLElement, offer: SubscriptionOffer, onUpdate: (next: SubscriptionOffer) => void): void {
  const button = root.querySelector<HTMLButtonElement>("#edit-wallet");
  const dialog = root.querySelector<HTMLDialogElement>("#wallet-dialog");
  const title = root.querySelector<HTMLElement>("#wallet-dialog-title");
  const body = root.querySelector<HTMLElement>("#wallet-dialog-body");
  const errorSlot = root.querySelector<HTMLElement>("#wallet-dialog-error");
  const continueBtn = root.querySelector<HTMLButtonElement>("#wallet-continue");
  const skipBtn = root.querySelector<HTMLButtonElement>("#wallet-skip");
  const closeBtn = root.querySelector<HTMLButtonElement>("#wallet-close");
  if (!button || !dialog || !continueBtn || !offer.payer_address || !offer.spender || !offer.token_contract) return;
  const previous = offer.payer_address;
  const signed = usesSignedTerms(offer);
  let updated: SubscriptionOffer | null = null;
  const showError = (message: string) => {
    if (errorSlot) errorSlot.innerHTML = errorBox(message);
    if (!dialog.open) dialog.showModal();
  };
  const showReplace = () => {
    updated = null;
    if (title) title.textContent = "Change wallet";
    if (body) {
      body.textContent = signed
        ? "The new wallet signs the subscription terms and approves the spending cap if needed. Future charges will be pulled from it, and the old wallet's terms are revoked."
        : "The new wallet will approve the current spending cap. Future charges will be pulled from it.";
    }
    continueBtn.textContent = "Continue";
    continueBtn.disabled = false;
    if (skipBtn) skipBtn.hidden = true;
    if (closeBtn) closeBtn.hidden = false;
    if (errorSlot) errorSlot.innerHTML = "";
  };
  const showRevoke = () => {
    if (title) title.textContent = "Revoke the previous wallet";
    if (body) {
      body.textContent = `The subscription now uses the new wallet. Remove its allowance on ${shortAddress(previous)} so that wallet can no longer be charged for it.`;
    }
    continueBtn.textContent = "Revoke allowance";
    continueBtn.disabled = false;
    if (skipBtn) skipBtn.hidden = false;
    if (closeBtn) closeBtn.hidden = true;
    if (errorSlot) errorSlot.innerHTML = "";
    if (!dialog.open) dialog.showModal();
  };
  button.addEventListener("click", () => {
    showReplace();
    dialog.showModal();
  });
  closeBtn?.addEventListener("click", () => dialog.close());
  skipBtn?.addEventListener("click", () => {
    dialog.close();
    if (updated) onUpdate(updated);
  });
  const pending = dialogStatus(
    root,
    dialog,
    root.querySelector<HTMLElement>("#wallet-dialog-form"),
    root.querySelector<HTMLElement>("#wallet-dialog-pending")
  );
  let walletSeq = 0;
  let pickAnother = false;
  continueBtn.addEventListener("click", () => {
    const seq = ++walletSeq;
    continueBtn.disabled = true;
    const fresh = pickAnother;
    pickAnother = false;
    void (async () => {
      try {
        // A new wallet is always picked in AppKit; the revoke step reuses the old one when it is connected.
        await pending.start(updated ? previous : null);
        if (!updated) {
          const cap = BigInt(offer.spending_cap_raw || offer.approve_amount_raw || "0");
          const { payer: connected, tx, signature } = await connectAndApprove({
            chainId: offer.chain_id,
            token: offer.token_contract,
            spender: offer.spender,
            amountRaw: cap,
            differentFrom: previous,
            freshWallet: fresh,
            activation: {
              paymentAmount: offer.approve_amount,
              tokenDecimals: offer.token_decimals,
              tokenSymbol: offer.token_symbol,
              purpose: "move the subscription to this wallet",
            },
            otherBudgets: (wallet) => fetchWalletBudget(offer.subscription_id, wallet),
            ...signedTermsStep(offer),
            isCancelled: () => seq !== walletSeq,
            onConnected: () => pending.connected(),
            onStatus: pending.status,
          });
          pending.status("Saving the new wallet…");
          updated = await replaceWallet(offer.subscription_id, connected, tx, signature);
          pending.reset();
          if (signed) {
            // The server revokes the old wallet's terms; that wallet can no longer be charged.
            dialog.close();
            onUpdate(updated);
            return;
          }
          showRevoke();
          return;
        }
        await revokeAllowance({
          chainId: offer.chain_id,
          token: offer.token_contract,
          spender: offer.spender,
          account: previous,
          otherBudgets: (wallet) => fetchWalletBudget(offer.subscription_id, wallet),
          isCancelled: () => seq !== walletSeq,
          onConnected: () => pending.connected(),
          onStatus: pending.status,
        });
        pending.reset();
        dialog.close();
        onUpdate(updated);
      } catch (error) {
        if (seq !== walletSeq) return;
        pending.reset();
        continueBtn.disabled = false;
        if (walletCancelled(error)) {
          if (!updated) showReplace();
          if (!dialog.open) dialog.showModal();
          return;
        }
        if (error instanceof InsufficientFundsError && !updated) {
          showReplace();
          pickAnother = true;
          continueBtn.textContent = "Connect another wallet";
          showError(error.message);
          return;
        }
        const msg = error instanceof Error ? error.message : "Could not change wallet";
        if (updated) showRevoke();
        else showReplace();
        showError(msg);
      }
    })();
  });
}

function bindRenewApproval(root: HTMLElement, offer: SubscriptionOffer, onUpdate: (next: SubscriptionOffer) => void): void {
  const button = root.querySelector<HTMLButtonElement>("#renew-approve");
  const note = root.querySelector<HTMLElement>("#renew-note");
  const slot = root.querySelector<HTMLElement>("#renew-pending");
  if (!button || !offer.payer_address || !offer.spender || !offer.token_contract) return;
  const needed = BigInt(offer.period_limit_raw || "0");
  const amount = BigInt(offer.approve_amount_raw || offer.period_limit_raw || "0");
  const show = (on: boolean) => {
    button.hidden = !on;
    if (note) note.hidden = !on;
  };
  show(shouldOfferRenew(offer.last_error_code, null, needed));
  void readAllowance({
    chainId: offer.chain_id,
    token: offer.token_contract,
    owner: offer.payer_address,
    spender: offer.spender,
  })
    .then((allowance) => {
      show(shouldOfferRenew(offer.last_error_code, allowance, needed));
    })
    .catch(() => {
      /* keep the last-error state */
    });
  let seq = 0;
  button.addEventListener("click", () => {
    if (amount <= 0n) return;
    const run = ++seq;
    button.disabled = true;
    show(false);
    const status = (message: string) => {
      if (slot) slot.innerHTML = loadingRow(message);
    };
    status("Opening wallet…");
    void (async () => {
      try {
        const { tx } = await connectAndApprove({
          chainId: offer.chain_id,
          token: offer.token_contract,
          spender: offer.spender,
          amountRaw: amount,
          account: offer.payer_address!,
          otherBudgets: (wallet) => fetchWalletBudget(offer.subscription_id, wallet),
          approveIfNeeded: true,
          isCancelled: () => run !== seq,
          onStatus: status,
        });
        status("Saving the approval…");
        onUpdate(await updateSpendingCap(offer.subscription_id, amount.toString(), tx));
      } catch (error) {
        if (run !== seq) return;
        if (slot) slot.innerHTML = "";
        button.disabled = false;
        show(true);
        if (walletCancelled(error)) return;
        const msg = error instanceof Error ? error.message : "Approve failed";
        root.querySelector("#renew-error")?.remove();
        button.insertAdjacentHTML("beforebegin", `<div id="renew-error">${errorBox(msg)}</div>`);
      }
    })();
  });
}

export function bindManage(root: HTMLElement, offer: SubscriptionOffer, onUpdate: (next: SubscriptionOffer) => void): void {
  dismissWalletModal();
  bindManageLink(root);
  bindSpendingCap(root, offer, onUpdate);
  bindRenewApproval(root, offer, onUpdate);
  bindWalletEdit(root, offer, onUpdate);
  root.querySelector("#retry-pay")?.addEventListener("click", () => {
    const btn = root.querySelector<HTMLButtonElement>("#retry-pay");
    if (btn) btn.disabled = true;
    void (async () => {
      try {
        onUpdate(await retryOffer(offer.subscription_id));
      } catch (error) {
        if (btn) btn.disabled = false;
        const msg = error instanceof Error ? error.message : "Retry failed";
        root.insertAdjacentHTML("beforeend", errorBox(msg));
      }
    })();
  });
  root.querySelector("#cancel-sub")?.addEventListener("click", () => {
    const btn = root.querySelector<HTMLButtonElement>("#cancel-sub");
    if (usesSignedTerms(offer)) {
      void cancelSignedTerms(root, offer, btn, onUpdate);
      return;
    }
    void (async () => {
      const choice = await confirmDialog({
        title: "Cancel this subscription?",
        body: `Your wallet will remove this subscription from the token allowance so future charges cannot be pulled.${accessNote(offer)}`,
        confirmLabel: "Revoke and cancel",
        cancelLabel: "Keep subscription",
        danger: true,
        alt: { prompt: "No access to the wallet?", label: "Skip it and cancel without revoking" },
      });
      if (!choice) return;
      const revocable = Boolean(offer.token_contract && offer.spender && offer.chain_id && offer.payer_address);
      const revoke = choice === "confirm" && revocable;
      if (btn) {
        btn.disabled = true;
        btn.textContent = revoke ? "Revoking allowance…" : "Cancelling…";
      }
      try {
        if (revoke) {
          showWalletPending(root, "Opening wallet…");
          await revokeAllowance({
            chainId: offer.chain_id,
            token: offer.token_contract,
            spender: offer.spender,
            account: offer.payer_address!,
            otherBudgets: (wallet) => fetchWalletBudget(offer.subscription_id, wallet),
            onStatus: (message) => showWalletPending(root, message),
          });
          clearWalletPending(root);
        }
        onUpdate(await cancelOffer(offer.subscription_id));
      } catch (error) {
        clearWalletPending(root);
        if (btn) {
          btn.disabled = false;
          btn.textContent = "Cancel subscription";
        }
        const msg = error instanceof Error ? error.message : "Cancel failed";
        root.insertAdjacentHTML("beforeend", errorBox(msg));
      }
    })();
  });
}

/** v2: the server revokes the signed terms on-chain, so cancelling needs no wallet. */
async function cancelSignedTerms(
  root: HTMLElement,
  offer: SubscriptionOffer,
  btn: HTMLButtonElement | null,
  onUpdate: (next: SubscriptionOffer) => void
): Promise<void> {
  const choice = await confirmDialog({
    title: "Cancel this subscription?",
    body: `The signed subscription terms are revoked on-chain, so no further charges can be pulled.${accessNote(offer)}`,
    confirmLabel: "Cancel subscription",
    cancelLabel: "Keep subscription",
    danger: true,
  });
  if (choice !== "confirm") return;
  if (btn) {
    btn.disabled = true;
    btn.textContent = "Cancelling…";
  }
  try {
    onUpdate(await cancelOffer(offer.subscription_id));
  } catch (error) {
    if (btn) {
      btn.disabled = false;
      btn.textContent = "Cancel subscription";
    }
    const msg = error instanceof Error ? error.message : "Cancel failed";
    root.insertAdjacentHTML("beforeend", errorBox(msg));
  }
}

export function manageLoading(label: string): string {
  return loadingRow(label);
}
